import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
  SimpleChanges,
  TemplateRef,
  input,
} from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import type { CurrentUser } from '@core/account-types';
import { QuickEvaluationContext } from '@core/analytics-legacy';
import { Analytics } from '@core/analytics-types';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { InterviewResultRequest } from '@core/interview-result-request-types';
import type {
  HiddenResultPreview,
  InterviewResult,
  RatingLevelTranslations,
} from '@core/interview-results-types';
import { InterviewGroupBy } from '@core/interview-results-types';
import { InterviewEvaluationRatingComponent } from '@core/interview-results-ui';
import { InterviewTemplateScope } from '@core/interview-templates-data';
import type { JobItem } from '@core/offers-types';
import type { PlacementOfferSelectOption } from '@core/offers-ui';
import { OfferSelectOptionHelper } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import type { Stage } from '@core/stages-types';
import { HiddenResultsActions } from '@core/unbiased-hiring-data';
import { HiddenEvaluationsInfoboxComponent } from '@core/unbiased-hiring-legacy';
import type { DialogRef, SelectGroupedOptions } from '@core/user-interface-legacy';
import {
  DividerComponent,
  HeadingComponent,
  IconComponent,
  RtStatefulComponent,
} from '@core/user-interface-legacy';

import { CachedQuickEvaluationFormOpenerDirective } from '../cached-quick-evaluation-form-opener.directive';
import { InterviewTemplateSelectService } from '../interview-template-select.service';
import { InterviewResultCardComponent } from './interview-result-card/interview-result-card.component';
import { InterviewResultRequestCardComponent } from './interview-result-request-card/interview-result-request-card.component';

type State = {
  expanded: boolean;
  currentAdmin: CurrentUser | null;
  offers: PlacementOfferSelectOption[];
  templates: SelectGroupedOptions[];
  hasPlacementFairEvalEnabled: Map<number, boolean>;
};

@Component({
  selector: 'rt-interview-list',
  templateUrl: 'interview-list.component.html',
  styleUrls: ['./interview-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeadingComponent,
    IconComponent,
    DividerComponent,
    InterviewEvaluationRatingComponent,
    HiddenEvaluationsInfoboxComponent,
    InterviewResultRequestCardComponent,
    InterviewResultCardComponent,
    TranslatePipe,
    CachedQuickEvaluationFormOpenerDirective,
    NgTemplateOutlet,
  ],
})
export class InterviewListComponent extends RtStatefulComponent<State> {
  private readonly store = inject(Store);
  private readonly analytics = inject(Analytics);

  private readonly interviewTemplateService = inject(InterviewTemplateSelectService);
  protected readonly hasEvaluationPanel = injectDevFlag(DevFlag.EvaluationImprovements);

  protected readonly cdRef: ChangeDetectorRef;
  public readonly i18n = inject(I18nService);

  public readonly dialogRef = input<DialogRef>(undefined!);
  public readonly hiddenEvaluationsInfoboxTpl = input<TemplateRef<unknown>>();
  public readonly type = input<'request' | 'result' | 'rating'>(undefined!);
  public readonly rating = input<null | number>();
  public readonly groupBy = input<InterviewGroupBy>();
  public readonly edit = input<boolean>(true);
  public readonly short = input<boolean>(false);
  public readonly hasTitle = input<boolean>(true);
  public readonly inCandidate = input<boolean>(false);
  public readonly openCandidateOnClick = input<boolean>(false);
  public readonly openEventModalOnClick = input<boolean>(false);
  public readonly inDashboard = input<boolean>(false);
  public readonly hasHeader = input<boolean>(false);
  public readonly hasMoreThanOneJob = input<boolean>(false);
  public readonly candidatePlacements = input<Placement[] | undefined>([]);
  public readonly currentUserAvailablePlacements = input<Placement[]>([]);
  public readonly hasAccessToAllJobOffers = input<boolean>();
  public readonly candidateId = input<number>();
  public readonly ratingLevelTranslations = input<RatingLevelTranslations>();
  @Input()
  public items: Array<InterviewResult | InterviewResultRequest> = [];
  @Input() public hiddenResultsPreview?: HiddenResultPreview[] = [];
  @Input() public offer?: JobItem | null;
  @Input() public title: string = '';

  public quickEvaluationClick = output<{
    offerId: number;
    context: QuickEvaluationContext;
  }>();

  public abilities = {
    viewCandidates: false,
    highLevelDataAccess: false,
    reviewHiddenEvaluations: false,
    manageCandidates: false,
  };
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      expanded: true,
      currentAdmin: null,
      templates: [],
      offers: [],
      hasPlacementFairEvalEnabled: new Map<number, boolean>(),
    });
  }

  public ngOnInit(): void {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(take(1), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentAdmin => {
        this.setState({
          ...this.state,
          currentAdmin,
        });
      });
    this.checkAbilities();
    this.getData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.candidatePlacements || changes.currentUserAvailablePlacements) {
      this.setState({
        ...this.state,
        offers: OfferSelectOptionHelper.placementsToOfferSelectOptions(
          this.currentUserAvailablePlacements(),
        ),
        hasPlacementFairEvalEnabled: this.reduceCandidatePlacementsToFairEvalStatus(),
      });
    }

    if (changes.items && this.candidatePlacements()?.length) {
      this.setState({
        ...this.state,
        hasPlacementFairEvalEnabled: this.reduceCandidatePlacementsToFairEvalStatus(),
      });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public hasPlacementFairEvalEnabled(offerId: number | null): boolean {
    if (offerId) {
      return this.state.hasPlacementFairEvalEnabled.get(offerId) === true;
    }

    return false;
  }

  public toggle(): void {
    this.setState({
      ...this.state,
      expanded: !this.state.expanded,
    });
  }

  public hasRating(): boolean {
    return this.rating() !== null || !!this.hiddenResultsPreview?.length;
  }

  public hasAccessToJob(offerId: number): boolean {
    return (
      this.hasAccessToAllJobOffers() ||
      this.currentUserAvailablePlacements().some(placement => placement.offerId === offerId)
    );
  }

  public onReviewHiddenEvaluationsClick(offer: JobItem, stage?: Stage) {
    const payload =
      this.groupBy() !== InterviewGroupBy.STAGE
        ? { candidateId: this.candidateId()!, offerId: offer.id }
        : {
            candidateId: this.candidateId()!,
            offerId: offer.id,
            stageId: stage?.id,
          };

    this.store.dispatch(
      HiddenResultsActions.hiddenEvaluationsReviewClick({
        payload,
      }),
    );
    this.analytics.track('[Evaluations] Review');
  }

  public get hiddenEvaluationsJobNameVisible(): boolean {
    if (this.hasMoreThanOneJob()) {
      return this.groupBy() !== 'stage' && this.hiddenResultsPreview!.length > 1;
    } else {
      return !this.groupBy();
    }
  }

  public isCurrentStage(offer: JobItem, stage: Stage): boolean {
    return (
      stage.id ===
      this.candidatePlacements()?.find(placement => placement.offerId === offer.id)?.stageId
    );
  }

  private reduceCandidatePlacementsToFairEvalStatus(): Map<number, boolean> {
    return (
      this.candidatePlacements()?.reduce(
        (accumulator, placement) =>
          accumulator.set(placement.offerId, !!placement.stage?.fairEvaluationsEnabled),
        new Map(),
      ) ?? new Map()
    );
  }

  private checkAbilities(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.abilities = {
          highLevelDataAccess: enabledMap[Ability.HighLevelDataAccess],
          viewCandidates: enabledMap[Ability.ViewCandidates],
          reviewHiddenEvaluations: enabledMap[Ability.ReviewHiddenEvaluations],
          manageCandidates: enabledMap[Ability.ManageCandidates],
        };
        this.cdRef.detectChanges();
      });
  }

  private getData(): void {
    this.interviewTemplateService
      .getInterviewTemplatesSelectOptions(InterviewTemplateScope.Interview)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(templates => this.setState({ ...this.state, templates }));
  }
}
